import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FaUsers, FaCalendarAlt, FaComments, FaImages, FaHistory,
  FaHeart, FaChartLine, FaGraduationCap, FaMoneyBillWave,
  FaCrown, FaArrowRight, FaArrowLeft, FaTimes, FaPlay,
  FaHome, FaLightbulb, FaQuoteLeft, FaGripHorizontal
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

// Inspirational quotes from prominent African Americans
const AFRICAN_AMERICAN_QUOTES = [
  {
    quote: "I am not tragically colored. There is no great sorrow dammed up in my soul, nor lurking behind my eyes. I do not belong to the sobbing school of Negrohood.",
    author: "Zora Neale Hurston",
    title: "Writer & Anthropologist"
  },
  {
    quote: "The time is always right to do what is right.",
    author: "Dr. Martin Luther King Jr.",
    title: "Civil Rights Leader"
  },
  {
    quote: "If you have no confidence in self, you are twice defeated in the race of life.",
    author: "Marcus Garvey",
    title: "Political Leader & Activist"
  },
  {
    quote: "Hold fast to dreams, for if dreams die, life is a broken-winged bird that cannot fly.",
    author: "Langston Hughes",
    title: "Poet & Social Activist"
  },
  {
    quote: "The family is the first essential cell of human society.",
    author: "Pope John XXIII, echoed by Dorothy Height",
    title: "Civil Rights Leader"
  },
  {
    quote: "Service is the rent we pay for being. It is the very purpose of life, and not something you do in your spare time.",
    author: "Marian Wright Edelman",
    title: "Founder, Children's Defense Fund"
  },
  {
    quote: "We delight in the beauty of the butterfly, but rarely admit the changes it has gone through to achieve that beauty.",
    author: "Maya Angelou",
    title: "Poet & Civil Rights Activist"
  },
  {
    quote: "Success is to be measured not so much by the position that one has reached in life as by the obstacles which he has overcome.",
    author: "Booker T. Washington",
    title: "Educator & Author"
  },
  {
    quote: "There is no better than adversity. Every defeat, every heartbreak, every loss, contains its own seed, its own lesson on how to improve your performance the next time.",
    author: "Malcolm X",
    title: "Minister & Human Rights Activist"
  },
  {
    quote: "I had to make my own living and my own opportunity. But I made it! Don't sit down and wait for the opportunities to come. Get up and make them.",
    author: "Madam C.J. Walker",
    title: "Entrepreneur & Philanthropist"
  },
  {
    quote: "Your legacy is every life you touch.",
    author: "Oprah Winfrey",
    title: "Media Executive & Philanthropist"
  },
  {
    quote: "We are the ones we've been waiting for. We are the change that we seek.",
    author: "Barack Obama",
    title: "44th President of the United States"
  },
  {
    quote: "There is no greater agony than bearing an untold story inside you.",
    author: "Maya Angelou",
    title: "Poet & Civil Rights Activist"
  },
  {
    quote: "When you've worked hard, and done well, and walked through that doorway of opportunity, you do not slam it shut behind you. You reach back.",
    author: "Michelle Obama",
    title: "Former First Lady"
  },
  {
    quote: "A people without the knowledge of their past history, origin and culture is like a tree without roots.",
    author: "Marcus Garvey",
    title: "Political Leader & Activist"
  },
  {
    quote: "Education is the passport to the future, for tomorrow belongs to those who prepare for it today.",
    author: "Malcolm X",
    title: "Minister & Human Rights Activist"
  },
  {
    quote: "I've learned that people will forget what you said, people will forget what you did, but people will never forget how you made them feel.",
    author: "Maya Angelou",
    title: "Poet & Civil Rights Activist"
  },
  {
    quote: "For I am my mother's daughter, and the drums of Africa still beat in my heart.",
    author: "Mary McLeod Bethune",
    title: "Educator & Civil Rights Leader"
  }
];

// Get a random quote for each step (but consistent per step)
const getQuoteForStep = (stepIndex) => {
  return AFRICAN_AMERICAN_QUOTES[stepIndex % AFRICAN_AMERICAN_QUOTES.length];
};

// Journey steps configuration
const JOURNEY_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Your Legacy Journey',
    icon: FaHeart,
    location: null, // No specific location - shows on current page
    targetSelector: null,
    content: {
      headline: "You're About to Create Something Timeless",
      description: "Legacy Forge Family isn't just an app — it's a private, invitation-only space connecting your past, present, and future generations. Every family has a story worth preserving, traditions worth passing down, and bonds worth strengthening.",
      useQuoteIndex: 14, // Marcus Garvey - "A people without knowledge of their past..."
      why: "Only people you invite can join your family. In the next few minutes, we'll show you how to build a lasting digital home — a place where memories live forever and connections grow stronger, protected within your family circle."
    }
  },
  {
    id: 'dashboard',
    title: 'Your Family Command Center',
    icon: FaHome,
    location: '/dashboard',
    targetSelector: '[data-testid="dashboard"]',
    spotlightPadding: 20,
    content: {
      headline: "This is Your Dashboard",
      description: "Your dashboard is mission control for your family legacy. From here, you can see all your family groups, create new ones, and access key features.",
      tip: "You can manage multiple family groups — your immediate family, extended family, or even different branches of your family tree."
    }
  },
  {
    id: 'create-family',
    title: 'Start Your Family Legacy',
    icon: FaUsers,
    location: '/dashboard',
    targetSelector: '[data-testid="create-family-btn"]',
    spotlightPadding: 10,
    content: {
      headline: "Create Your First Family Group",
      description: "Every legacy begins with a single step. Creating a family group establishes the foundation where your family's story will unfold.",
      useQuoteIndex: 10, // Oprah - "Your legacy is every life you touch"
      why: "This isn't just a group — it's a digital heirloom that can be passed down through generations. Your great-grandchildren could one day explore the memories you create today.",
      tip: "Start with your immediate family, then expand to include extended relatives over time."
    }
  },
  {
    id: 'family-overview',
    title: 'Your Family Hub',
    icon: FaUsers,
    location: 'family', // Special: will navigate to first family
    targetSelector: '[data-testid="family-details"]',
    spotlightPadding: 0,
    fullPage: true,
    content: {
      headline: "Welcome to Your Family Space",
      description: "This is where your family comes together. Each tab represents a different aspect of your family life — from planning events to preserving memories.",
      why: "This is a private, invitation-only space. Only people you specifically invite (or add as proxy members) can see or participate in your family's activities. Your family's story stays within your family."
    }
  },
  {
    id: 'events-tab',
    title: 'Plan Meaningful Gatherings',
    icon: FaCalendarAlt,
    location: 'family',
    targetSelector: '[data-testid="tab-events"]',
    spotlightPadding: 8,
    content: {
      headline: "Family Events",
      description: "Reunions, birthdays, holidays, graduations — every gathering is an opportunity to strengthen bonds and create memories.",
      useQuoteIndex: 13, // Michelle Obama - "reach back"
      why: "Research shows that regular family gatherings increase children's emotional wellbeing and sense of identity. You're not just planning parties — you're building your family's culture.",
      tip: "Start with one annual reunion or regular gathering. Consistency builds tradition. Use one of the powerful templates to help ensure that all steps are included in your plans."
    }
  },
  {
    id: 'members-tab',
    title: 'Build Your Family Tree',
    icon: FaUsers,
    location: 'family',
    targetSelector: '[data-testid="tab-members"]',
    spotlightPadding: 8,
    content: {
      headline: "Family Members — Your Inner Circle",
      description: "This is an invitation-only system. Only people you explicitly invite can join and participate in your family's activities.",
      useQuoteIndex: 17, // Mary McLeod Bethune - "drums of Africa"
      why: "Your family's privacy is paramount. There are two ways to add members:",
      features: [
        { icon: FaUsers, name: "Email Invitation", desc: "Send an invite — they create their own account and join" },
        { icon: FaUsers, name: "Proxy Members", desc: "Add family members who don't have email (children, elders) — you manage their profile" }
      ],
      tip: "Start by inviting the 'connectors' in your family — those who stay in touch with everyone. They can help spread the word."
    }
  },
  {
    id: 'chat-tab',
    title: 'Stay Connected Daily',
    icon: FaComments,
    location: 'family',
    targetSelector: '[data-testid="tab-chat"]',
    spotlightPadding: 8,
    content: {
      headline: "Family Chat",
      description: "Real-time messaging keeps your family connected between gatherings. Share updates, celebrate wins, and support each other.",
      useQuoteIndex: 16, // Maya Angelou - "how you made them feel"
      why: "Families who communicate regularly report stronger relationships and better conflict resolution. This is your family's private space away from social media noise.",
      tip: "Use chat for casual updates. Save important announcements for events or dedicated discussions."
    }
  },
  {
    id: 'gallery-link',
    title: 'Preserve Your Memories',
    icon: FaImages,
    location: 'family',
    targetSelector: '[data-testid="gallery-link"]',
    spotlightPadding: 8,
    content: {
      headline: "Photo Gallery",
      description: "Upload photos, create albums, and build a visual history of your family. Every image tells part of your story.",
      useQuoteIndex: 12, // Maya Angelou - "untold story"
      why: "Photos are the most powerful triggers for memory and emotion. The gallery you build today becomes tomorrow's treasure trove of family history.",
      tip: "Create albums for each event. Add captions to help future generations understand the context."
    }
  },
  {
    id: 'timeline-link',
    title: 'Your Family Story',
    icon: FaHistory,
    location: 'family',
    targetSelector: '[data-testid="timeline-link"]',
    spotlightPadding: 8,
    content: {
      headline: "Family Timeline",
      description: "Document milestones, achievements, and significant moments. Your timeline becomes a living history of your family.",
      useQuoteIndex: 7, // Booker T. Washington - "obstacles overcome"
      why: "Children who know their family history have higher self-esteem and stronger sense of identity. Your timeline is a gift to future generations.",
      tip: "Add historical events too — when grandparents immigrated, when the family business started, important firsts."
    }
  },
  {
    id: 'premium-preview',
    title: 'Unlock Your Full Potential',
    icon: FaCrown,
    location: 'family',
    targetSelector: null,
    content: {
      headline: "Premium Features Await",
      description: "As your family grows, Legacy Forge grows with you. Premium tiers unlock powerful features:",
      features: [
        { icon: FaCrown, name: "Family Roles", desc: "Assign responsibilities and leadership" },
        { icon: FaMoneyBillWave, name: "Dues Management", desc: "Organize family finances together" },
        { icon: FaHeart, name: "Spiritual Corner", desc: "Share faith, prayers, and values" },
        { icon: FaChartLine, name: "Family Analytics", desc: "Insights into your family's engagement" },
        { icon: FaGraduationCap, name: "Professional Resources", desc: "Access to financial advisors, attorneys, and more" }
      ],
      tip: "You can explore these features anytime — locked features show what's possible when you upgrade."
    }
  },
  {
    id: 'completion',
    title: "You're Ready!",
    icon: FaLightbulb,
    location: null,
    targetSelector: null,
    content: {
      headline: "Your Legacy Journey Begins Now",
      description: "You now have everything you need to start building your family's digital legacy. Remember:",
      tips: [
        "Start small — invite a few key family members first",
        "Plan one event to bring everyone together",
        "Upload a few photos to spark memories",
        "Check in regularly to keep the momentum going"
      ],
      closing: "Every great family legacy started with someone who decided to bring everyone together. Today, that someone is you.",
      useQuoteIndex: 11 // Barack Obama - "We are the ones we've been waiting for"
    }
  }
];

// Spotlight overlay component
function SpotlightOverlay({ targetRect, padding = 10 }) {
  if (!targetRect) {
    return (
      <div className="fixed inset-0 bg-black/40 z-[9998]" />
    );
  }

  const { top, left, width, height } = targetRect;
  const paddedTop = top - padding;
  const paddedLeft = left - padding;
  const paddedWidth = width + padding * 2;
  const paddedHeight = height + padding * 2;

  return (
    <div className="fixed inset-0 z-[9998]">
      <svg className="w-full h-full">
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={paddedLeft}
              y={paddedTop}
              width={paddedWidth}
              height={paddedHeight}
              rx="8"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.40)"
          mask="url(#spotlight-mask)"
        />
      </svg>
      {/* Highlight border around target */}
      <div
        className="absolute border-2 border-blue-400 rounded-lg shadow-[0_0_20px_rgba(59,130,246,0.5)] pointer-events-none"
        style={{
          top: paddedTop,
          left: paddedLeft,
          width: paddedWidth,
          height: paddedHeight
        }}
      />
    </div>
  );
}

// Journey card component
function JourneyCard({ step, currentIndex, totalSteps, onNext, onPrev, onSkip, targetRect }) {
  const Icon = step.icon;
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === totalSteps - 1;
  
  // Draggable state - use absolute pixel positions for stability
  const [position, setPosition] = useState(null); // null means centered
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);

  // Lock body scroll when component mounts
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Reset position to center when step changes
  useEffect(() => {
    setPosition(null); // Reset to centered
  }, [currentIndex]);

  // Get quote for this step if specified
  const stepQuote = step.content.useQuoteIndex !== undefined 
    ? AFRICAN_AMERICAN_QUOTES[step.content.useQuoteIndex] 
    : null;

  // Unified handler for starting drag (mouse or touch)
  const handleDragStart = (clientX, clientY, target) => {
    if (target.closest('button')) return false; // Don't drag when clicking buttons
    
    // If first drag, calculate current center position
    const rect = cardRef.current?.getBoundingClientRect();
    if (rect && position === null) {
      setPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      });
    }
    
    setIsDragging(true);
    setDragStart({ x: clientX, y: clientY });
    return true;
  };

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    handleDragStart(e.clientX, e.clientY, e.target);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !position) return;
    e.preventDefault(); // Prevent any default behavior
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    setPosition(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY
    }));
    setDragStart({ x: e.clientX, y: e.clientY });
  }, [isDragging, dragStart, position]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch drag handlers for mobile
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    handleDragStart(touch.clientX, touch.clientY, e.target);
  };

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || !position) return;
    e.preventDefault(); // Prevent background scrolling
    const touch = e.touches[0];
    const deltaX = touch.clientX - dragStart.x;
    const deltaY = touch.clientY - dragStart.y;
    setPosition(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY
    }));
    setDragStart({ x: touch.clientX, y: touch.clientY });
  }, [isDragging, dragStart, position]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add/remove global mouse listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Calculate style based on whether we have a drag position or are centered
  // On mobile, position higher and use more conservative max-height
  const cardStyle = position ? {
    top: position.y,
    left: position.x,
    transform: 'translate(-50%, -50%)',
    maxHeight: 'calc(100vh - 120px)', // Room for Emergent badge
    display: 'flex',
    flexDirection: 'column',
    userSelect: isDragging ? 'none' : 'auto',
    transition: isDragging ? 'none' : 'top 0.2s ease, left 0.2s ease'
  } : {
    top: '42%', // Position higher to give room at bottom
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxHeight: 'calc(100vh - 120px)', // Room for Emergent badge
    display: 'flex',
    flexDirection: 'column',
    userSelect: 'auto',
    transition: 'none'
  };

  return (
    <div 
      ref={cardRef}
      className={`fixed z-[9999] w-[90vw] max-w-[500px] bg-white rounded-2xl shadow-2xl overflow-hidden ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
      style={cardStyle}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-4 sm:px-6 py-3 sm:py-4">
        {/* Drag handle indicator - hidden on small mobile screens */}
        <div className="hidden sm:flex justify-center mb-2">
          <div className="flex items-center gap-1 text-white/50 text-xs">
            <FaGripHorizontal className="text-sm" />
            <span>Drag to move</span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon className="text-white text-sm sm:text-lg" />
            </div>
            <div className="min-w-0">
              <h3 className="text-white font-bold text-sm sm:text-lg truncate">{step.title}</h3>
              <p className="text-blue-100 text-xs sm:text-sm">Step {currentIndex + 1} of {totalSteps}</p>
            </div>
          </div>
          {/* Close button - larger tap target on mobile */}
          <button
            onClick={onSkip}
            className="text-white bg-white/20 hover:bg-white/30 active:bg-white/40 transition p-2 sm:p-2.5 rounded-full ml-2 flex-shrink-0"
            title="Skip tutorial"
            data-testid="legacy-journey-close-btn"
          >
            <FaTimes className="text-lg sm:text-xl" />
          </button>
        </div>
        {/* Progress bar */}
        <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-white transition-all duration-500"
            style={{ width: `${((currentIndex + 1) / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-5 overflow-y-auto flex-1">
        <h4 className="text-xl font-bold text-gray-900 mb-3">
          {step.content.headline}
        </h4>
        
        <p className="text-gray-800 mb-4 leading-relaxed">
          {step.content.description}
        </p>

        {/* African American Inspirational Quote */}
        {stepQuote && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-600 p-4 mb-4 rounded-r-lg">
            <FaQuoteLeft className="text-amber-600 mb-2" />
            <p className="text-gray-900 italic leading-relaxed font-medium">"{stepQuote.quote}"</p>
            <div className="mt-2">
              <p className="text-amber-800 font-bold text-sm">— {stepQuote.author}</p>
              <p className="text-amber-700 text-xs font-medium">{stepQuote.title}</p>
            </div>
          </div>
        )}

        {step.content.why && (
          <div className="bg-blue-50 p-4 rounded-lg mb-4 border border-blue-200">
            <p className="text-blue-900 text-sm leading-relaxed">
              <span className="font-bold">Why this matters: </span>
              {step.content.why}
            </p>
          </div>
        )}

        {step.content.features && (
          <div className="space-y-2 mb-4">
            {step.content.features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg border border-gray-200">
                <feature.icon className="text-blue-700" />
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{feature.name}</p>
                  <p className="text-gray-700 text-xs">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {step.content.tips && (
          <div className="space-y-2 mb-4">
            {step.content.tips.map((tip, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <span className="text-green-600 mt-1 font-bold">✓</span>
                <p className="text-gray-800 text-sm">{tip}</p>
              </div>
            ))}
          </div>
        )}

        {step.content.tip && (
          <div className="flex items-start gap-2 text-sm bg-yellow-50 p-3 rounded-lg border border-yellow-200">
            <FaLightbulb className="text-yellow-600 mt-0.5 flex-shrink-0" />
            <p className="text-gray-800"><span className="font-semibold text-gray-900">Tip:</span> {step.content.tip}</p>
          </div>
        )}

        {step.content.closing && (
          <p className="text-gray-900 font-semibold mt-4 text-center leading-relaxed">
            {step.content.closing}
          </p>
        )}
      </div>

      {/* Footer - Mobile-optimized with safe area for Emergent badge */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-t flex items-center justify-between flex-shrink-0 safe-area-bottom">
        <button
          onClick={onPrev}
          disabled={isFirstStep}
          className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg transition text-sm sm:text-base ${
            isFirstStep 
              ? 'text-gray-300 cursor-not-allowed' 
              : 'text-gray-600 hover:bg-gray-200 active:bg-gray-300'
          }`}
          data-testid="legacy-journey-prev-btn"
        >
          <FaArrowLeft />
          <span>Back</span>
        </button>

        <button
          onClick={onNext}
          className="flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition font-medium text-sm sm:text-base"
          data-testid="legacy-journey-next-btn"
        >
          <span>{isLastStep ? "Start Building!" : "Next"}</span>
          {!isLastStep && <FaArrowRight />}
        </button>
      </div>
    </div>
  );
}

// Main Legacy Journey component
export function LegacyJourney({ isOpen, onClose, onComplete }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [familyId, setFamilyId] = useState(null);

  const currentStep = JOURNEY_STEPS[currentStepIndex];

  // Reset to beginning when journey opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStepIndex(0);
      setTargetRect(null);
    }
  }, [isOpen]);

  // Fetch first family ID for family-specific steps
  useEffect(() => {
    const fetchFamily = async () => {
      try {
        const response = await fetch(`/api/families`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token')}`
          }
        });
        const families = await response.json();
        if (families.length > 0) {
          setFamilyId(families[0].family_id);
        }
      } catch (error) {
        console.error('Error fetching families:', error);
      }
    };
    if (isOpen && user) {
      fetchFamily();
    }
  }, [isOpen, user]);

  // Navigate to step location and find target element
  useEffect(() => {
    if (!isOpen) return;

    const step = JOURNEY_STEPS[currentStepIndex];
    let targetLocation = step.location;

    // Handle special 'family' location
    if (targetLocation === 'family' && familyId) {
      targetLocation = `/family/${familyId}`;
    } else if (targetLocation === 'family' && !familyId) {
      targetLocation = '/dashboard';
    }

    // Navigate if needed
    if (targetLocation && location.pathname !== targetLocation) {
      navigate(targetLocation);
    }

    // Find and highlight target element
    const findTarget = () => {
      if (step.targetSelector) {
        const element = document.querySelector(step.targetSelector);
        if (element) {
          const rect = element.getBoundingClientRect();
          setTargetRect(rect);
          
          // Scroll element into view if needed
          if (rect.top < 0 || rect.bottom > window.innerHeight) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Re-measure after scroll
            setTimeout(() => {
              const newRect = element.getBoundingClientRect();
              setTargetRect(newRect);
            }, 500);
          }
        } else {
          setTargetRect(null);
        }
      } else {
        setTargetRect(null);
      }
    };

    // Delay to allow navigation and rendering
    const timer = setTimeout(findTarget, 500);
    return () => clearTimeout(timer);
  }, [currentStepIndex, isOpen, location.pathname, navigate, familyId]);

  const handleNext = useCallback(() => {
    if (currentStepIndex < JOURNEY_STEPS.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      // Journey complete
      localStorage.setItem('legacyJourneyCompleted', 'true');
      if (onComplete) onComplete();
      onClose();
    }
  }, [currentStepIndex, onClose, onComplete]);

  const handlePrev = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  const handleSkip = useCallback(() => {
    localStorage.setItem('legacyJourneySkipped', 'true');
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="legacy-journey">
      <SpotlightOverlay 
        targetRect={targetRect} 
        padding={currentStep.spotlightPadding || 10} 
      />
      <JourneyCard
        step={currentStep}
        currentIndex={currentStepIndex}
        totalSteps={JOURNEY_STEPS.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={handleSkip}
        targetRect={targetRect}
      />
    </div>
  );
}

// Button to trigger the journey
export function StartJourneyButton({ className = '' }) {
  const [showJourney, setShowJourney] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowJourney(true)}
        className={`flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition font-medium shadow-lg ${className}`}
        data-testid="start-journey-btn"
      >
        <FaPlay className="text-sm" />
        <span>Start Legacy Journey</span>
      </button>
      <LegacyJourney 
        isOpen={showJourney} 
        onClose={() => setShowJourney(false)} 
      />
    </>
  );
}

export default LegacyJourney;
